import { legacy } from '@/shared/api/client';
import type {
  Segmentation,
  SegmentationChannel,
  SegmentationCreatePayload,
  SegmentationCriteria,
  SegmentationFieldDefinition,
  SegmentationUpdatePayload,
} from '@/shared/types/segmentation';

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

interface Envelope<T> {
  data: T;
}

export async function fetchSegmentations(idPassbook: string): Promise<Segmentation[]> {
  const { mergeUploadedAudiences } = await import('../model/materialize-upload');
  try {
    const res = await legacy.get<Envelope<Segmentation[]>>(`/segmentations/${idPassbook}`, passbookHeaders(idPassbook));
    const remote = Array.isArray(res.data) ? res.data : [];
    return mergeUploadedAudiences(idPassbook, remote);
  } catch (err) {
    const local = mergeUploadedAudiences(idPassbook, []);
    if (local.length > 0) return local;
    throw err;
  }
}

export async function fetchSegmentation(id: string, idPassbook: string): Promise<Segmentation> {
  const res = await legacy.get<Envelope<Segmentation>>(`/segmentations/detail/${id}`, passbookHeaders(idPassbook));
  return res.data;
}

export async function createSegmentation(payload: SegmentationCreatePayload): Promise<Segmentation> {
  const res = await legacy.post<Envelope<Segmentation>>(
    '/segmentations',
    payload,
    passbookHeaders(payload.id_passbook),
  );
  return res.data;
}

export async function updateSegmentation(
  id: string,
  idPassbook: string,
  payload: SegmentationUpdatePayload,
): Promise<Segmentation> {
  const res = await legacy.put<Envelope<Segmentation>>(
    `/segmentations/${id}`,
    payload,
    passbookHeaders(idPassbook),
  );
  return res.data;
}

export async function deleteSegmentation(id: string, idPassbook: string): Promise<void> {
  await legacy.delete<Envelope<{ deleted: boolean }>>(`/segmentations/${id}`, passbookHeaders(idPassbook));
}

export async function duplicateSegmentation(id: string, idPassbook: string): Promise<Segmentation> {
  const res = await legacy.post<Envelope<Segmentation>>(
    `/segmentations/${id}/duplicate`,
    undefined,
    passbookHeaders(idPassbook),
  );
  return res.data;
}

/** Botão manual "Atualizar público-alvo" -- nunca automático (ver Segmentation.resolvedSerialNumbers). */
export async function refreshSegmentationAudience(id: string, idPassbook: string): Promise<Segmentation> {
  const res = await legacy.post<Envelope<Segmentation>>(
    `/segmentations/${id}/refresh-audience`,
    undefined,
    passbookHeaders(idPassbook),
  );
  return res.data;
}

/** Contagem AO VIVO usada só no construtor (debounced) -- nunca persiste. */
export async function previewSegmentationCount(
  idPassbook: string,
  criteria: SegmentationCriteria,
  channel: SegmentationChannel,
): Promise<number> {
  const res = await legacy.post<Envelope<{ total: number }>>(
    '/segmentations/preview-count',
    { idPassbook, criteria, channel },
    passbookHeaders(idPassbook),
  );
  return res.data.total;
}

export interface SegmentationSampleLead {
  _id: string;
  profile?: { name?: string };
  email?: string;
  phone?: string;
  serialNumber?: string;
  storeName?: string;
}

/** "Amostra de Leads" (drawer) -- AO VIVO, nunca persiste. */
export async function fetchSegmentationSample(
  idPassbook: string,
  criteria: SegmentationCriteria,
  channel: SegmentationChannel,
  limit = 10,
): Promise<SegmentationSampleLead[]> {
  const res = await legacy.post<Envelope<SegmentationSampleLead[]>>(
    '/segmentations/sample-leads',
    { idPassbook, criteria, channel, limit },
    passbookHeaders(idPassbook),
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchSegmentationFieldCatalog(idPassbook: string): Promise<SegmentationFieldDefinition[]> {
  const res = await legacy.get<Envelope<SegmentationFieldDefinition[]>>(
    `/segmentations/fields/${idPassbook}`,
    passbookHeaders(idPassbook),
  );
  return Array.isArray(res.data) ? res.data : [];
}
